import { useMemo } from "react"
import Checkbox from "@/v2-ui/checkbox"
import Separator from "@/v2-ui/separator"
import Table, { createColumns } from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import { PENDING_CHANGE_LABEL } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import { pluralize } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

export type LicenseFeatureGroupPickTableProps = {
  featureGroups: readonly FeatureGroupFragment[]
  /** The draft. */
  selectedIds: ReadonlySet<number>
  /** As persisted; a row whose draft differs is marked as a change. */
  persistedIds: ReadonlySet<number>
  /**
   * A package that is not a choice — an add-on's own — listed first, ticked
   * and still, with why.
   */
  pinnedId?: number | null
  pinnedLabel?: string
  /** Whether the whole list is read-only (a save in flight). */
  isSaving?: boolean
  onToggle(featureGroupId: number): void
}

/** "10 licenses and 2 add-ons" — who else gives the package. */
function grantorsOf(featureGroup: FeatureGroupFragment) {
  const parts = [
    featureGroup.PlanCount > 0 && pluralize(featureGroup.PlanCount, "license"),
    featureGroup.AddOnCount > 0 && pluralize(featureGroup.AddOnCount, "add-on"),
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(" and ") : "nothing yet"
}

function createPickColumns(input: {
  selectedIds: ReadonlySet<number>
  persistedIds: ReadonlySet<number>
  pinnedId: number | null
  pinnedLabel: string
  isSaving: boolean
  onToggle(featureGroupId: number): void
}) {
  const {
    selectedIds,
    persistedIds,
    pinnedId,
    pinnedLabel,
    isSaving,
    onToggle,
  } = input
  const isGranted = (featureGroup: FeatureGroupFragment) =>
    featureGroup.Id === pinnedId || selectedIds.has(featureGroup.Id)
  const wasGranted = (featureGroup: FeatureGroupFragment) =>
    featureGroup.Id === pinnedId || persistedIds.has(featureGroup.Id)

  return createColumns<FeatureGroupFragment>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      IsGranted: {
        id: "IsGranted",
        label: "Granted",
        description:
          "Ticked: buying this grants the package. The tick is the draft; Save writes the ticked set as one declaration.",
        defaultSize: 72,
        enableHiding: false,
        getValue: (row) => isGranted(row),
        renderCell({ row }) {
          const featureGroup = row.original
          const isPinned = featureGroup.Id === pinnedId
          return (
            <Checkbox
              checked={isGranted(featureGroup)}
              disabled={isPinned || isSaving}
              aria-label={`Grant ${featureGroup.Name}`}
              onCheckedChange={() => {
                onToggle(featureGroup.Id)
              }}
            />
          )
        },
        exportValue({ row }) {
          return isGranted(row.original)
        },
      },
      Name: {
        id: "Name",
        label: "Package",
        defaultSize: 210,
        // Push: the row lives inside a panel, so the package panel stacks
        // on top of it (breadcrumb back).
        to: ({ row }) => `>>?v=featureGroup:${row.original.Id}`,
        renderCell({ row }) {
          const featureGroup = row.original
          const isPinned = featureGroup.Id === pinnedId
          const granted = isGranted(featureGroup)
          const isChanged = granted !== wasGranted(featureGroup)
          return (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{featureGroup.Name}</span>
              {isPinned && (
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {pinnedLabel}
                </span>
              )}
              {isChanged && (
                <span className="shrink-0 text-[11px] text-primary">
                  {granted
                    ? PENDING_CHANGE_LABEL.added
                    : PENDING_CHANGE_LABEL.removed}
                </span>
              )}
            </span>
          )
        },
      },
      Description: {
        id: "Description",
        label: "Description",
        defaultSize: 220,
        enableSorting: false,
        renderCell({ row }) {
          const value = row.original.Description
          if (!value) return null
          // The cell truncates and reveals the full text on hover itself
          // (TableBodyCell); the span brings no truncation of its own.
          return <span className="text-muted-foreground">{value}</span>
        },
      },
      FeatureCount: {
        id: "FeatureCount",
        label: "Features",
        description:
          "What the package grants: features picked one by one and features reached by its rules, today.",
        defaultSize: 84,
        renderCell({ row }) {
          const { FeatureCount, RuleCount } = row.original
          return (
            <span className="tabular-nums">
              {FeatureCount}
              {RuleCount > 0 && (
                <span className="ml-1 text-[11px] text-muted-foreground">
                  · {pluralize(RuleCount, "rule")}
                </span>
              )}
            </span>
          )
        },
      },
      GrantedBy: {
        id: "GrantedBy",
        label: "Granted by",
        description: "Which licenses and add-ons give the package today.",
        defaultSize: 125,
        getValue: (row) => grantorsOf(row),
        renderCell({ row }) {
          return (
            <span className="text-muted-foreground">
              {grantorsOf(row.original)}
            </span>
          )
        },
      },
      Code: {
        id: "Code",
        label: "Code",
        defaultSize: 160,
        defaultHidden: true,
      },
    },
  })
}

// The package vocabulary as a table to tick — the shared body of "what does
// this license grant" and "what else does this add-on unlock". One row per
// package: the tick (the draft), the name linking to the package's own
// panel for its contents, its description, what it grants and who else
// grants it. A pending change is said on the row, as the composer says it.
// The catalog is small, so the rows are all in view; the table brings the
// sorting, the columns and the export along for free.
function LicenseFeatureGroupPickTable(
  props: LicenseFeatureGroupPickTableProps,
) {
  const {
    featureGroups,
    selectedIds,
    persistedIds,
    pinnedId = null,
    pinnedLabel = "this add-on's own",
    isSaving = false,
    onToggle,
  } = props

  // The pinned package first, then by name: the row an operator cannot
  // change should not hide among the ones they can.
  const rows = useMemo(
    () =>
      [...featureGroups].sort((left, right) =>
        left.Id === pinnedId
          ? -1
          : right.Id === pinnedId
            ? 1
            : left.Name.localeCompare(right.Name),
      ),
    [featureGroups, pinnedId],
  )
  const columns = useMemo(
    () =>
      createPickColumns({
        selectedIds,
        persistedIds,
        pinnedId,
        pinnedLabel,
        isSaving,
        onToggle,
      }),
    [selectedIds, persistedIds, pinnedId, pinnedLabel, isSaving, onToggle],
  )
  const grantedCount = rows.filter(
    (featureGroup) =>
      featureGroup.Id === pinnedId || selectedIds.has(featureGroup.Id),
  ).length

  return (
    <Table
      // Sized by its rows, up to the height the tab hands down; the content
      // scrolls inside when the catalogue outgrows it, the footer stays put.
      className="license-feature-group-pick-table min-h-0"
      tableId="license-feature-group-pick-table"
      data={rows}
      columns={columns}
      getRowId={(row) => String(row.Id)}
      enableRowSelection={false}
    >
      <Table.Toolbar>
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {grantedCount} of {pluralize(rows.length, "package")} granted
        </span>
        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by package..." />
        <Table.ToolbarFilters />
        <Separator orientation="vertical" />
        <Table.ToolbarMenu exportFileName="packages" />
      </Table.Toolbar>
      <Table.FilterList />
      <Table.Content
        renderEmpty={
          <p className="p-4 text-sm text-muted-foreground">
            No packages exist yet — create them on the Licensing page&apos;s
            Packages tab first.
          </p>
        }
      />
    </Table>
  )
}

export default LicenseFeatureGroupPickTable
